import React from "react";
import { useOutletContext } from "react-router-dom";
import { KeyValue, Note, PageHead, Panel, RailCard, DeskSupport } from "@/components/portal/crm/kit";
import { LenderCriteriaForm } from "@/components/portal/LenderCriteriaForm";
import { CapacityCard } from "@/components/hub/CapacityCard";
import { AdminPreviewBanner } from "@/pages/introducer/Portal";
import { gbp } from "@/lib/stages";

export default function Criteria() {
  const { me, setMe, isAdmin } = useOutletContext();

  return (
    <>
      {isAdmin && <AdminPreviewBanner what="lender desk" />}

      <PageHead
        eyebrow="What you want to see"
        title="Your criteria"
        lede="These filters gate what Connexis routes to you. The tighter they are, the better the fit score on everything that lands."
        testId="lender-criteria-head"
      />

      <div className="grid items-start gap-7 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-7">
          <CapacityCard scope="lender" />
          <LenderCriteriaForm me={me} onSaved={(m) => setMe(m)} />
        </div>

        <div className="space-y-4">
          <RailCard title="How routing reads this" testId="lender-criteria-how">
            <ul className="space-y-3 text-[12.5px] leading-relaxed text-slate-600">
              {[
                "Finance type and deal size are hard filters — outside them, a case never reaches you.",
                "Sectors and your written criteria are scored, not enforced, so a strong case can still arrive with a note.",
                "Your turnaround is scored against how urgent the case is. Leaving it blank costs you fit points on every deal.",
                "Paused or over capacity still shows you the case, flagged, rather than quietly skipping your desk.",
              ].map((x) => <li key={x} className="flex gap-2.5"><span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-[#0055FF]" />{x}</li>)}
            </ul>
          </RailCard>

          <RailCard title="Your agreement with us" testId="lender-criteria-terms">
            <KeyValue label="Connexis fee" value={me?.terms_short || "—"} />
            <KeyValue label="Your typical fee" value={me?.expected_fee_pct != null ? `${me.expected_fee_pct}% of facility` : "Not set"} />
            <KeyValue label="Payment terms" value={`${me?.payment_terms_days ?? 14} days`} />
            <KeyValue label="Deal band" value={`${gbp(me?.min_deal_size_gbp)} – ${me?.max_deal_size_gbp ? gbp(me.max_deal_size_gbp) : "no cap"}`} />
            <KeyValue label="Agreement" value={me?.agreement?.status === "signed" ? "Signed" : "Awaiting you"} tone={me?.agreement?.status === "signed" ? "green" : undefined} />
          </RailCard>

          <DeskSupport body="Easier to say than to tick? Tell us in plain words what you want and we'll set the filters for you." />
          <Note>Changing your fee model is a paperwork change, not a settings change — that one goes through the desk.</Note>
        </div>
      </div>
    </>
  );
}
